import { useUser } from "@clerk/clerk-react";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  AttachFile as AttachFileIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  CameraAlt as ScreenshotIcon,
  Send as SendIcon,
  Support as SupportIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { type ChangeEvent, type KeyboardEvent, useEffect, useRef, useState } from "react";
import { LoadingBox } from "@/components/ui";
import { useSupportChatContext } from "../contexts/support-chat-context";
import { useSupportChat } from "../hooks/use-support-chat";
import type { SupportConversation, SupportMessage } from "../types";
import { clickableProps } from "../utils/keyboard";

const DRAWER_WIDTH = 480;

const RECENT_MESSAGE_THRESHOLD_MS = 15000; // 15 seconds

function MessageItem({
  message,
  onImageClick,
  userImageUrl,
  showAvatar,
  isRecent,
}: {
  message: SupportMessage;
  onImageClick: (url: string) => void;
  userImageUrl?: string;
  showAvatar: boolean;
  isRecent: boolean;
}) {
  const isCustomer = message.sender_type === "customer";
  const avatarSrc = isCustomer ? userImageUrl : message.sender_avatar || undefined;

  // Determine background color based on sender and recent status
  const getBgColor = (theme: { palette: { mode: string } }) => {
    if (isRecent && !isCustomer) {
      // Subtle yellow for recent support messages
      return theme.palette.mode === "dark"
        ? "rgba(255, 236, 179, 0.15)"
        : "rgba(255, 243, 224, 0.8)";
    }
    if (isCustomer) {
      return theme.palette.mode === "dark"
        ? "rgba(144, 202, 249, 0.08)"
        : "rgba(25, 118, 210, 0.04)";
    }
    return "transparent";
  };

  const getHoverBgColor = (theme: { palette: { mode: string } }) => {
    if (isRecent && !isCustomer) {
      return theme.palette.mode === "dark" ? "rgba(255, 236, 179, 0.2)" : "rgba(255, 243, 224, 1)";
    }
    if (isCustomer) {
      return theme.palette.mode === "dark"
        ? "rgba(144, 202, 249, 0.12)"
        : "rgba(25, 118, 210, 0.08)";
    }
    return "action.hover";
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        px: 2,
        py: showAvatar ? 0.75 : 0.25,
        bgcolor: getBgColor,
        "&:hover": {
          bgcolor: getHoverBgColor,
          "& .message-timestamp": {
            opacity: 1,
          },
        },
        transition: "background-color 0.5s ease",
      }}
    >
      {/* Avatar - only show for first message in consecutive group */}
      {showAvatar ? (
        <Avatar
          src={avatarSrc}
          sx={{
            width: 36,
            height: 36,
            bgcolor: isCustomer ? "primary.main" : "grey.500",
            fontSize: 14,
          }}
        />
      ) : (
        <Box sx={{ width: 36, flexShrink: 0 }} />
      )}

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Header: Name - only show for first message */}
        {showAvatar && (
          <Typography
            variant="body2"
            component="span"
            sx={{
              fontWeight: 600,
              color: isCustomer ? "primary.main" : "text.primary",
            }}
          >
            {isCustomer ? (
              "You"
            ) : (
              <>
                {message.sender_name || "Support Team"}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{ color: "text.secondary", fontWeight: 400 }}
                >
                  {" "}
                  from desplega.ai
                </Typography>
              </>
            )}
          </Typography>
        )}

        {/* Message Content with timestamp on hover */}
        <Stack direction="row" alignItems="baseline" spacing={1}>
          {message.content && (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                mt: showAvatar ? 0.25 : 0,
                color: "text.primary",
                lineHeight: 1.5,
              }}
            >
              {message.content}
            </Typography>
          )}
          <Typography
            className="message-timestamp"
            variant="caption"
            sx={{
              color: "text.secondary",
              opacity: 0,
              transition: "opacity 0.15s ease",
              fontSize: "0.7rem",
              flexShrink: 0,
            }}
          >
            {new Date(message.created_at).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </Typography>
        </Stack>

        {/* Image Attachment */}
        {message.image_url && (
          <Box
            component="img"
            src={message.image_url}
            alt="Attachment"
            {...clickableProps(() => onImageClick(message.image_url!))}
            onClick={() => onImageClick(message.image_url!)}
            sx={{
              maxWidth: "60%",
              maxHeight: 150,
              borderRadius: 1,
              mt: message.content ? 0.75 : 0.25,
              cursor: "pointer",
              border: 1,
              borderColor: "divider",
              "&:hover": {
                opacity: 0.9,
                borderColor: "primary.main",
              },
              transition: "all 0.15s ease",
            }}
          />
        )}
      </Box>
    </Box>
  );
}

function ConversationHistoryList({
  conversations,
  currentConversationId,
  onSelect,
  onBack,
}: {
  conversations: SupportConversation[];
  currentConversationId: string | null;
  onSelect: (conversation: SupportConversation) => void;
  onBack: () => void;
}) {
  return (
    <Box sx={{ flex: 1, overflowY: "auto" }}>
      <Stack
        direction="row"
        alignItems="center"
        sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <IconButton aria-label="Back" onClick={onBack} size="small" sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="subtitle1" fontWeight={600}>
          History
        </Typography>
      </Stack>
      {conversations.length === 0 ? (
        <Stack alignItems="center" sx={{ py: 4, px: 2, textAlign: "center" }}>
          <HistoryIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1.5 }} />
          <Typography variant="body2" color="text.secondary">
            No previous conversations
          </Typography>
        </Stack>
      ) : (
        <Box>
          {conversations.map((conversation) => (
            <Box
              key={conversation.id}
              {...clickableProps(() => onSelect(conversation))}
              onClick={() => onSelect(conversation)}
              sx={{
                px: 2,
                py: 1.25,
                cursor: "pointer",
                bgcolor:
                  conversation.id === currentConversationId ? "action.selected" : "transparent",
                "&:hover": {
                  bgcolor:
                    conversation.id === currentConversationId ? "action.selected" : "action.hover",
                },
                borderBottom: 1,
                borderColor: "divider",
                transition: "background-color 0.15s ease",
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    flex: 1,
                    fontWeight: conversation.id === currentConversationId ? 600 : 400,
                  }}
                >
                  {conversation.subject || "Support Request"}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  {conversation.conversation_status === "closed" && (
                    <Chip
                      label="Closed"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 10,
                        bgcolor: "action.hover",
                      }}
                    />
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {new Date(conversation.created_at).toLocaleDateString([], {
                      month: "short",
                      day: "numeric",
                    })}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function SupportChat() {
  const { isOpen, setIsOpen } = useSupportChatContext();
  const { user } = useUser();
  const {
    conversationId,
    currentConversation,
    messages,
    conversationHistory,
    showHistory,
    setShowHistory,
    isLoading,
    isLoadingHistory,
    isSending,
    isUploading,
    isCapturingScreenshot,
    isClosing,
    sendMessage,
    sendMessageWithImage,
    captureAndSendScreenshot,
    startConversation,
    closeConversation,
    switchConversation,
    messagesEndRef,
  } = useSupportChat();
  const [input, setInput] = useState("");

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [enlargedImageUrl, setEnlargedImageUrl] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [lastUserMessageTime, setLastUserMessageTime] = useState<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Update "now" every 3 seconds to recalculate which messages are recent
  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleSend = async () => {
    if ((!input.trim() && !selectedImage) || isSending) return;
    const content = input.trim();
    setInput("");

    // Mark that user sent a message - this clears recent highlights on previous messages
    setLastUserMessageTime(Date.now());

    if (selectedImage) {
      const file = selectedImage;
      setSelectedImage(null);
      setImagePreview(null);
      await sendMessageWithImage(content, file);
    } else {
      await sendMessage(content);
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file?.type.startsWith("image/")) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleScreenshot = async () => {
    await captureAndSendScreenshot();
  };

  const clearImagePreview = () => {
    setSelectedImage(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
      setImagePreview(null);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = async () => {
    await startConversation();
  };

  const handleCloseConversation = async () => {
    await closeConversation();
  };

  const isCurrentConversationClosed = currentConversation?.conversation_status === "closed";

  return (
    <>
      {/* Chat Panel */}
      <Slide direction="left" in={isOpen} mountOnEnter unmountOnExit>
        <Box
          className="support-chat-container"
          data-screenshot-ignore="true"
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: { xs: "100vw", md: DRAWER_WIDTH },
            height: "100vh",
            bgcolor: "background.paper",
            zIndex: 1200,
            display: "flex",
            flexDirection: "column",
            boxShadow: 3,
          }}
        >
          {/* Header */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Box>
              <Typography variant="h6">Support Chat</Typography>
              <Typography variant="caption" color="text.secondary">
                Expect &lt;30min response during working hours ·{" "}
                <Box
                  component="a"
                  href="mailto:contact@desplega.ai"
                  sx={{
                    color: "primary.main",
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  contact@desplega.ai
                </Box>
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Conversation History">
                <IconButton
                  aria-label="Conversation History"
                  onClick={() => setShowHistory(true)}
                  size="small"
                  color={showHistory ? "primary" : "default"}
                >
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="New Conversation">
                <IconButton
                  aria-label="New Conversation"
                  onClick={handleNewConversation}
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <IconButton aria-label="Close" onClick={() => setIsOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>

          {/* Content Area */}
          {showHistory ? (
            <ConversationHistoryList
              conversations={conversationHistory}
              currentConversationId={conversationId}
              onSelect={(conv) => switchConversation(conv)}
              onBack={() => setShowHistory(false)}
            />
          ) : (
            <>
              {/* Current conversation info bar - show when there's a conversation with messages */}
              {conversationId && messages.length > 0 && (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "action.hover",
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 48,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ flex: 1, minWidth: 0, mr: 1 }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      noWrap
                      sx={{ fontWeight: 500 }}
                    >
                      {(() => {
                        const firstMsg = messages[0]?.content;
                        if (!firstMsg) return "Support Request";
                        return firstMsg.length > 50 ? `${firstMsg.slice(0, 50)}...` : firstMsg;
                      })()}
                    </Typography>
                    {isCurrentConversationClosed && (
                      <Chip
                        label="Closed"
                        size="small"
                        color="default"
                        sx={{ height: 20, fontSize: 11, flexShrink: 0 }}
                      />
                    )}
                  </Stack>
                  {!isCurrentConversationClosed && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleCloseConversation}
                      disabled={isClosing}
                      startIcon={isClosing ? <CircularProgress size={14} /> : <CheckCircleIcon />}
                      sx={{ fontSize: 12, flexShrink: 0 }}
                    >
                      Resolve
                    </Button>
                  )}
                </Stack>
              )}

              {/* Messages */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  pb: 2,
                }}
              >
                {isLoading || isLoadingHistory ? (
                  <LoadingBox size={24} sx={{ py: 4, p: 0 }} />
                ) : messages.length === 0 ? (
                  <Stack
                    alignItems="center"
                    justifyContent="center"
                    sx={{ height: "100%", px: 3, textAlign: "center" }}
                  >
                    <SupportIcon sx={{ fontSize: 48, color: "text.disabled", mb: 2 }} />
                    <Typography variant="body1" fontWeight={500} gutterBottom>
                      Need help?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280 }}>
                      Send us a message and we'll get back to you as soon as possible.
                    </Typography>
                  </Stack>
                ) : (
                  messages.map((message, index) => {
                    const prevMessage = messages[index - 1];
                    const showAvatar =
                      !prevMessage || prevMessage.sender_type !== message.sender_type;
                    const messageTime = new Date(message.created_at).getTime();
                    // Message is recent if: < 30s old AND arrived after user's last sent message
                    const isRecent =
                      now - messageTime < RECENT_MESSAGE_THRESHOLD_MS &&
                      messageTime > lastUserMessageTime;
                    return (
                      <MessageItem
                        key={message.id}
                        message={message}
                        onImageClick={setEnlargedImageUrl}
                        userImageUrl={user?.imageUrl}
                        showAvatar={showAvatar}
                        isRecent={isRecent}
                      />
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </Box>

              <Divider />

              {/* Input */}
              {isCurrentConversationClosed ? (
                <Stack alignItems="center" sx={{ p: 2, bgcolor: "action.hover" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    This conversation is closed
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleNewConversation}
                    startIcon={<AddIcon />}
                  >
                    Start New Conversation
                  </Button>
                </Stack>
              ) : (
                <Stack sx={{ p: 2 }} spacing={1}>
                  {/* Image Preview */}
                  {imagePreview && (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{
                        p: 1,
                        bgcolor: "action.hover",
                        borderRadius: 1,
                      }}
                    >
                      <Box
                        component="img"
                        src={imagePreview}
                        alt="Preview"
                        sx={{
                          height: 60,
                          borderRadius: 1,
                          objectFit: "cover",
                        }}
                      />
                      <Typography variant="caption" sx={{ flex: 1 }} noWrap>
                        {selectedImage?.name}
                      </Typography>
                      <IconButton
                        aria-label="Remove image"
                        size="small"
                        onClick={clearImagePreview}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  )}

                  {/* Input Row */}
                  <Stack direction="row" alignItems="flex-end" spacing={1}>
                    {/* Hidden file input */}
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      style={{ display: "none" }}
                    />

                    {/* Attach Image Button */}
                    <Tooltip title="Attach image">
                      <IconButton
                        aria-label="Attach image"
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isSending || isUploading}
                      >
                        <AttachFileIcon />
                      </IconButton>
                    </Tooltip>

                    {/* Screenshot Button */}
                    <Tooltip title="Take screenshot">
                      <IconButton
                        aria-label="Take screenshot"
                        size="small"
                        onClick={handleScreenshot}
                        disabled={isSending || isCapturingScreenshot}
                      >
                        {isCapturingScreenshot ? (
                          <CircularProgress size={20} />
                        ) : (
                          <ScreenshotIcon />
                        )}
                      </IconButton>
                    </Tooltip>

                    <TextField
                      fullWidth
                      multiline
                      maxRows={4}
                      placeholder="Type a message..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isSending}
                      size="small"
                    />
                    <IconButton
                      aria-label="Send message"
                      color="primary"
                      onClick={handleSend}
                      disabled={(!input.trim() && !selectedImage) || isSending}
                    >
                      {isSending ? <CircularProgress size={24} /> : <SendIcon />}
                    </IconButton>
                  </Stack>
                </Stack>
              )}
            </>
          )}
        </Box>
      </Slide>

      {/* Enlarged Image Dialog */}
      <Dialog
        open={!!enlargedImageUrl}
        onClose={() => setEnlargedImageUrl(null)}
        maxWidth="lg"
        PaperProps={{
          sx: { bgcolor: "transparent", boxShadow: "none" },
        }}
      >
        {enlargedImageUrl && (
          <Box
            component="img"
            src={enlargedImageUrl}
            alt="Enlarged"
            {...clickableProps(() => setEnlargedImageUrl(null))}
            onClick={() => setEnlargedImageUrl(null)}
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
              cursor: "pointer",
            }}
          />
        )}
      </Dialog>
    </>
  );
}
